import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Input, Button, Flex, Space, Empty } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import useDebounce from "../../hooks/useDebounce";
import { notify, serverError } from "../../utils/notify";

/**
 * Reusable admin table.
 *
 * SERVER mode  → pass `fetcher({ page, pageSize, search, sortField, sortOrder, ...filters })`
 *                that resolves to `{ data, total }` (a bare array is also accepted).
 * CLIENT mode  → pass `dataSource` (array) + `searchableKeys` for in-memory search.
 *
 * Common props: columns, rowKey, searchPlaceholder, toolbar (extra filter controls),
 * filters (server-side filter object — refetches on change), refreshKey, onRow,
 * defaultPageSize, size, scrollX.
 */
export default function DataTable({
  columns,
  rowKey = "_id",
  fetcher,
  dataSource,
  searchableKeys = [],
  searchPlaceholder = "Search…",
  toolbar,
  filters,
  refreshKey,
  onRow,
  defaultPageSize = 10,
  size = "middle",
  scrollX = 900,
  showSearch = true,
  emptyText = "No data",
  rowSelection,
}) {
  const isServer = typeof fetcher === "function";

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  const [serverData, setServerData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const filterKey = JSON.stringify(filters || {});

  // Reset to page 1 whenever the query (search/filters) changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterKey]);

  const load = useCallback(async () => {
    if (!isServer) return;
    setLoading(true);
    try {
      const res = await fetcher({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sortField,
        sortOrder,
        ...(filters || {}),
      });
      if (Array.isArray(res)) {
        setServerData(res);
        setTotal(res.length);
      } else {
        setServerData(res?.data || []);
        setTotal(res?.total ?? (res?.data ? res.data.length : 0));
      }
    } catch (e) {
      notify.error(serverError(e, "Failed to load data"));
      setServerData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServer, page, pageSize, debouncedSearch, sortField, sortOrder, filterKey, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  // CLIENT mode: filter in-memory by searchableKeys.
  const clientFiltered = useMemo(() => {
    if (isServer) return [];
    const arr = dataSource || [];
    if (!debouncedSearch || searchableKeys.length === 0) return arr;
    const q = debouncedSearch.toLowerCase();
    return arr.filter((row) =>
      searchableKeys.some((k) =>
        String(row?.[k] ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServer, dataSource, debouncedSearch, searchableKeys.join(","), filterKey]);

  const handleTableChange = (pagination, _tableFilters, sorter) => {
    if (isServer) {
      setPage(pagination.current);
      setPageSize(pagination.pageSize);
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      setSortField(s?.field);
      setSortOrder(s?.order ? (s.order === "ascend" ? "asc" : "desc") : undefined);
    }
  };

  const paginationConfig = isServer
    ? {
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
        pageSizeOptions: [10, 20, 50, 100],
      }
    : {
        pageSize: defaultPageSize,
        showSizeChanger: true,
        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
        pageSizeOptions: [10, 20, 50, 100],
      };

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 14 }}>
        <Space wrap>
          {showSearch && (
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#9aa5b8" }} />}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
          )}
          {toolbar}
        </Space>
        {isServer && (
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        )}
      </Flex>

      <Table
        columns={columns}
        rowKey={rowKey}
        dataSource={isServer ? serverData : clientFiltered}
        loading={loading}
        onChange={handleTableChange}
        onRow={onRow}
        rowSelection={rowSelection}
        size={size}
        scroll={{ x: scrollX }}
        pagination={paginationConfig}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} /> }}
        sticky
      />
    </div>
  );
}
